import React, { useReducer, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
} from 'components';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  updateMaCisCreator,
  setMaCisEditErrorsCreator,
  loadGlobalCisLevelDomainCreator,
} from '../../../../../store/actionsCreators';
import reducer, { defaultState, updateFieldCreator, setEditDataCreator } from './reducer';

const EditForecast = ({ errors, updateForecast, removeError, levelDomains, forecast, loadLevelDomains, certificationBody }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const history = useHistory();

  const currentYear = new Date().getFullYear();
  const yearOptions = [
    { value: currentYear, label: currentYear.toString() },
    { value: currentYear + 1, label: (currentYear + 1).toString() }
  ];

  useEffect(() => {
    if (forecast) {
      dispatch(setEditDataCreator(forecast));
    }
  }, [forecast]);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  useEffect(() => {
    loadLevelDomains();
  }, []);

  const submit = () => {
    const params = {
      id: forecast.id,
      year: state.year,
      newAdded: state.newAdded,
      renewAdded: state.renewAdded,
      levelDomain: state.levelDomain,
      certificationBody: certificationBody,
    };
    updateForecast(state.id, params, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.forecasts.editForecast')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="levelDomain"
                  label={t('page.user.cis.forecasts.levelDomain')}
                  required
                  error={errors.levelDomain}
                >
                  <Select
                    id="levelDomain"
                    options={levelDomains}
                    value={levelDomains?.find(levelDomain => levelDomain.value === state.levelDomain)}
                    onChange={handleChangeSelect('levelDomain', 'levelDomain')}
                    placeholder={t('page.user.cis.forecasts.levelDomainPlaceholder')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="year"
                  label={t('page.user.cis.forecasts.year')}
                  required
                  error={errors.year}
                >
                  <Select
                    id="year"
                    options={yearOptions}
                    value={yearOptions.find(year => year.value === state.year)}
                    onChange={handleChangeSelect('year', 'year')}
                    placeholder={t('page.user.cis.forecasts.yearPlaceholder')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="newAdded"
                  label={t('page.user.cis.forecasts.newAdded')}
                  required
                  error={errors.newAdded}
                >
                  <input
                    id="newAdded"
                    type="number"
                    placeholder={t('page.user.cis.forecasts.newAddedPlaceholder')}
                    value={state.newAdded}
                    onChange={handleChangeNative('newAdded', 'newAdded')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="renewAdded"
                  label={t('page.user.cis.forecasts.renewAdded')}
                  required
                  error={errors.renewAdded}
                >
                  <input
                    id="renewAdded"
                    type="number"
                    placeholder={t('page.user.cis.forecasts.renewAddedPlaceholder')}
                    value={state.renewAdded}
                    onChange={handleChangeNative('renewAdded', 'renewAdded')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={submit}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

EditForecast.propTypes = {
  errors: PropTypes.object,
  updateForecast: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  levelDomains: PropTypes.array.isRequired,
  forecast: PropTypes.object,
  certificatioBody: PropTypes.number,
};

EditForecast.defaultProps = {
  errors: {},
  forecast: {},
};

const mapStateToProps = ({ ma, globals, user }) => ({
  errors: ma.cis.forecasts.editErrors || {},
  levelDomains: globals.cis.levelDomains,
  forecast: ma.cis.forecasts.profile || {},
  certificationBody: user.user.memberAssociation.certificationBody.id
});

const mapDispatchToProps = (dispatch) => ({
  updateForecast: (id, params, history) =>
    dispatch(updateMaCisCreator(id, 'forecasts', params, history)),
  removeError: (errors) => dispatch(setMaCisEditErrorsCreator('forecasts', errors)),
  loadLevelDomains: () => dispatch(loadGlobalCisLevelDomainCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditForecast); 